import { FC, useEffect, useState } from "react";
import { Player } from "@/models/gameGeneral";
import { TeamReference } from "@/models/pdfData";
import { Box, Divider, List, Typography } from "@mui/material";
import { gameState, useTeamSides } from "@/services/gameState";
import { useAppDataContext } from "@context/AppContext";

const RefereeTabletPregameTeamStats: FC = () => {
  const { openSettingsDialog } = useAppDataContext();
  const teamSquads = gameState("teamSquads");
  const gender = gameState("gender");

  const [genderizedPlayersName, setGenderizedPlayersName] =
    useState("Spieler:innen");
  const [genderizedLiberosName, setGenderizedLiberosName] = useState("Liberos");
  const [genderizedCaptainName, setGenderizedCaptainName] =
    useState("Kapitän:in");

  const [amountPlayers, setAmountPlayers] = useState<ValueTypeNumber>(
    defaultValueTypeNumber(),
  );
  const [amountBench, setAmountBench] = useState<ValueTypeNumber>(
    defaultValueTypeNumber(),
  );
  const [amountLiberos, setAmountLiberos] = useState<ValueTypeNumber>(
    defaultValueTypeNumber(),
  );
  const [gameCaptain, setGameCaptain] = useState<ValueTypePlayer>({
    team1: undefined,
    team2: undefined,
  });

  useEffect(() => {
    if (gender) {
      setGenderizedPlayersName(gender === "MALE" ? "Spieler" : "Spielerinnen");
      setGenderizedLiberosName(gender === "MALE" ? "Liberos" : "Libera");
      setGenderizedCaptainName(gender === "MALE" ? "Kapitän" : "Kapitänin");
    }
  }, [gender]);

  useEffect(() => {
    if (teamSquads) {
      setAmountPlayers({
        team1: teamSquads.team1.players.length,
        team2: teamSquads.team2.players.length,
      });
      setAmountBench({
        team1: teamSquads.team1.officials.length,
        team2: teamSquads.team2.officials.length,
      });
      setAmountLiberos({
        team1: teamSquads.team1.liberos.length,
        team2: teamSquads.team2.liberos.length,
      });
      setGameCaptain({
        team1: teamSquads.team1.captain,
        team2: teamSquads.team2.captain,
      });
    }
  }, [teamSquads]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        fontSize: "5vw",
      }}
      onClick={openSettingsDialog}
    >
      <List
        sx={{
          width: "40%",
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Divider
          component={"li"}
          variant={"fullWidth"}
          flexItem
          sx={{ gridColumn: "1/4" }}
        />
        <RemainingRow name={genderizedPlayersName} value={amountPlayers} />
        <Divider
          component={"li"}
          variant={"fullWidth"}
          flexItem
          sx={{ gridColumn: "1/4" }}
        />
        <RemainingRow name={"Bank"} value={amountBench} />
        <Divider
          component={"li"}
          variant={"fullWidth"}
          flexItem
          sx={{ gridColumn: "1/4" }}
        />
        <RemainingRow name={genderizedLiberosName} value={amountLiberos} />
        <Divider
          component={"li"}
          variant={"fullWidth"}
          flexItem
          sx={{ gridColumn: "1/4" }}
        />
        <RemainingRow name={genderizedCaptainName} value={gameCaptain} />
        <Divider
          component={"li"}
          variant={"fullWidth"}
          flexItem
          sx={{ gridColumn: "1/4" }}
        />
      </List>
    </Box>
  );
};

type ValueTypeNumber = { [key in TeamReference]: number };
type ValueTypePlayer = { [key in TeamReference]: Player | undefined };

const defaultValueTypeNumber = (): ValueTypeNumber => ({ team1: 0, team2: 0 });

const RemainingRow: FC<{
  name: string;
  value: ValueTypeNumber | ValueTypePlayer;
}> = ({ name, value }) => {
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());

  const isNumber = typeof value.team1 === "number";

  const valueLeft = isNumber
    ? value[leftTeam]?.toString()
    : !!value[leftTeam]
      ? `#${(value[leftTeam] as Player).jerseyNumber}`
      : "-";
  const valueRight = isNumber
    ? value[rightTeam]?.toString()
    : !!value[rightTeam]
      ? `#${(value[rightTeam] as Player).jerseyNumber}`
      : "-";

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          textAlign: "center",
          my: "2px",
          px: 1,
          borderTopLeftRadius: "0.4em",
          borderBottomLeftRadius: "0.4em",
        }}
      >
        <Typography fontSize={"1em"}>{valueLeft}</Typography>
      </Box>
      <Box sx={{ textAlign: "center", flexBasis: "2.2em" }}>
        <Typography>{name}</Typography>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          textAlign: "center",
          my: "2px",
          px: 1,
          borderTopRightRadius: "0.4em",
          borderBottomRightRadius: "0.4em",
        }}
      >
        <Typography fontSize={"1em"}>{valueRight}</Typography>
      </Box>
    </>
  );
};

export default RefereeTabletPregameTeamStats;
